import { useDispatch, useSelector } from 'react-redux'
import type { FirestoreReducer, WhereOptions } from 'react-redux-firebase'
import { isEmpty, isLoaded, useFirestoreConnect } from 'react-redux-firebase'
import { push } from 'connected-react-router'
import { useCallback } from 'react'
import type { Apartment } from 'types/apartment'
import type { Company } from 'types/company'
import type {
	PropertyManagersCertificate,
	PropertyManagersCertificateStep,
} from '@viilu-web/shared/types/property-managers-certificate'
import type { Document } from '@viilu-web/shared/types/document'

type FirestoreState = FirestoreReducer.Reducer

export const usePropertyManagersCertificates = (
	managerUUID: string,
	filters?: {
		step?: PropertyManagersCertificateStep
	},
) => {
	const storeAs = `property_managers_certificates_list_${managerUUID}_${
		filters?.step ?? 'all'
	}`

	useFirestoreConnect(() => {
		return [
			{
				collection: 'property-managers-certificate',
				storeAs,
				where: [
					['managerUUID', '==', managerUUID],
					filters?.step ? ['step', '==', filters.step] : null,
				].filter((v): v is WhereOptions => !!v),
			},
		]
	})

	return useSelector(
		({ firestoreReducer }: { firestoreReducer: FirestoreState }) => {
			if (!firestoreReducer.ordered) {
				return { loading: true, propertyManagersCertificates: [] }
			}

			const propertyManagersCertificates = firestoreReducer.ordered[
				storeAs
			] as PropertyManagersCertificate[]

			if (!isLoaded(propertyManagersCertificates)) {
				return { loading: true, propertyManagersCertificates }
			}
			return { loading: false, propertyManagersCertificates }
		},
	)
}

export const usePropertyManagersNavigationLogic = () => {
	const dispatch = useDispatch()

	const setActivePropertyManagersCertificate = useCallback(
		(uuid: string | null) => {
			const params = new URLSearchParams(window.location.search)
			if (!uuid) {
				params.delete('cuuid')
			} else {
				params.set('cuuid', uuid)
			}
			dispatch(push(`?${params.toString()}`))
		},
		[dispatch],
	)

	return { setActivePropertyManagersCertificate }
}

// TODO: This maybe should be in a re-usable place
export const useManagerCompanies = (managerUUID: string) => {
	useFirestoreConnect(() => {
		return [
			{
				collection: 'company',
				storeAs: `manager_companies_list_${managerUUID}`,
				where: [['managerUUID', '==', managerUUID]],
			},
		]
	})

	return useSelector(
		({ firestoreReducer }: { firestoreReducer: FirestoreState }) => {
			if (!firestoreReducer.ordered) {
				return { loading: true, companies: [] }
			}

			const companies = firestoreReducer.ordered[
				`manager_companies_list_${managerUUID}`
			] as Company[]

			if (!isLoaded(companies)) {
				return { loading: true, companies }
			}
			return { loading: false, companies }
		},
	)
}

// TODO: This maybe should be in a re-usable place
export const useCompanyApartments = (companyUUID_?: string | null) => {
	const companyUUID = companyUUID_ || 'no-value'
	useFirestoreConnect(() => {
		return [
			{
				collection: 'apartment',
				storeAs: `company_apartments_list_${companyUUID}`,
				where: [['companyUUID', '==', companyUUID]],
			},
		]
	})

	return useSelector(
		({ firestoreReducer }: { firestoreReducer: FirestoreState }) => {
			if (!firestoreReducer.ordered) {
				return { loading: true, apartments: [] }
			}

			const apartments = firestoreReducer.ordered[
				`company_apartments_list_${companyUUID}`
			] as Apartment[]

			if (!isLoaded(apartments)) {
				return { loading: true, apartments }
			}
			return { loading: false, apartments }
		},
	)
}

export const usePmCertDocuments = (cert: PropertyManagersCertificate) => {
	const { managerUUID, uuid } = cert

	const storeAs = `property-managers-certificate-documents-${managerUUID}`

	useFirestoreConnect(() => {
		return [
			{
				collection: 'document',
				storeAs,
				where: [
					['companyUUID', '==', managerUUID || 'empty'], // We use the shadow company for this
					['parentUUID', '==', uuid || 'empty'],
					['origin', '==', 'property-managers-certificate'],
				],
				limit: 25,
			},
		]
	})

	const document = useSelector(
		({ firestoreReducer }: { firestoreReducer: FirestoreState }) =>
			firestoreReducer.ordered[storeAs],
	)

	const empty: Document[] = []
	if (!isLoaded(document)) {
		return { loading: true, documents: empty }
	}
	if (isEmpty(document)) {
		return { loading: false, documents: empty }
	}

	return { loading: false, documents: document as Document[] }
}
